import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Apiservice } from '../../../../services/ApiService';
import {
  ScanTestMasterConfig,
  ScanTestRecord,
  buildScanTestKey,
  isMasterConfig
} from '../../../../models/scan-testing.model';
import { Performance } from '../../../../models/performance.model';

interface CopyTarget {
  timestamp: number;
  description: string;
  date: string;
  hasEmails: boolean;
  status?: 'pending' | 'saving' | 'done' | 'error';
  error?: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-alerts',
  templateUrl: './alerts.component.html',
  standalone: false,
  styleUrls: ['./alerts.component.scss']
})
export class AlertsComponent implements OnInit {
  @Input() venueID = '';
  @Input() venueTimezone = 'Australia/Sydney';
  @Input() performance!: Performance;
  @Input() otherPerformances: Performance[] = [];

  @Output() closed = new EventEmitter<void>();

  loading = true;
  loadError = '';
  saving = false;
  saveError = '';

  emails: string[] = [];
  emailDraft = '';
  emailDraftError = '';

  view: 'edit' | 'copy' = 'edit';
  copyLoading = false;
  copyTargets: CopyTarget[] = [];
  selectedTargets = new Set<number>();
  copyRunning = false;
  copyError = '';
  copyDone = false;

  private masterRecord: ScanTestMasterConfig | null = null;
  private scanTestKey = '';

  constructor(private apiservice: Apiservice) {}

  ngOnInit(): void {
    this.scanTestKey = buildScanTestKey(this.venueID, Number(this.performance.item));
    this.loadMaster();
  }

  private loadMaster() {
    this.loading = true;
    this.loadError = '';
    this.apiservice.getScanTests(this.venueID, Number(this.performance.item)).subscribe({
      next: (records) => {
        const master = (records as ScanTestRecord[]).find((r): r is ScanTestMasterConfig => isMasterConfig(r));
        this.masterRecord = master ?? null;
        this.emails = [...(master?.notificationEmails ?? [])];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadError = err?.error?.message ?? 'Failed to load alert config.';
      }
    });
  }

  trackEmail(_idx: number, email: string) { return email; }
  trackTarget(_idx: number, t: CopyTarget) { return t.timestamp; }

  addEmail() {
    const value = this.emailDraft.trim();
    if (!value) { this.emailDraftError = 'Enter an email address.'; return; }
    if (!EMAIL_RE.test(value)) { this.emailDraftError = 'Invalid email format.'; return; }
    if (this.emails.includes(value)) { this.emailDraftError = 'Already added.'; return; }
    this.emails = [...this.emails, value];
    this.emailDraft = '';
    this.emailDraftError = '';
  }

  removeEmail(email: string) {
    this.emails = this.emails.filter((e) => e !== email);
  }

  save() {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';
    const next: ScanTestMasterConfig = {
      key: this.scanTestKey,
      item: 0,
      schedule: this.masterRecord?.schedule ?? [],
      timezone: this.masterRecord?.timezone ?? (this.venueTimezone || 'Australia/Sydney'),
      notificationEmails: [...this.emails]
    };
    this.apiservice.postScanTest(next).subscribe({
      next: () => {
        this.masterRecord = next;
        this.saving = false;
        this.closed.emit();
      },
      error: (err) => {
        this.saving = false;
        this.saveError = err?.error?.message ?? 'Failed to save alert config.';
      }
    });
  }

  cancel() {
    if (this.saving || this.copyRunning) { return; }
    this.closed.emit();
  }

  enterCopyView() {
    if (this.saving || !this.emails.length) { return; }
    this.view = 'copy';
    this.copyDone = false;
    this.copyError = '';
    this.selectedTargets.clear();
    this.loadCopyTargets();
  }

  exitCopyView() {
    if (this.copyRunning) { return; }
    this.view = 'edit';
    this.copyTargets = [];
    this.selectedTargets.clear();
    this.copyError = '';
    this.copyDone = false;
  }

  private loadCopyTargets() {
    this.copyLoading = true;
    if (!this.otherPerformances.length) {
      this.copyTargets = [];
      this.copyLoading = false;
      return;
    }
    const calls = this.otherPerformances.map((p) =>
      this.apiservice.getScanTests(this.venueID, Number(p.item)).pipe(
        catchError(() => of<ScanTestRecord[]>([]))
      )
    );
    forkJoin(calls).subscribe({
      next: (results) => {
        this.copyTargets = this.otherPerformances.map((p, idx) => {
          const master = (results[idx] as ScanTestRecord[]).find((r): r is ScanTestMasterConfig => isMasterConfig(r));
          const hasEmails = !!(master?.notificationEmails?.length);
          return {
            timestamp: Number(p.item),
            description: p.description || '',
            date: this.formatVenueDate(Number(p.item)),
            hasEmails,
            status: 'pending'
          };
        });
        this.copyLoading = false;
      },
      error: () => { this.copyLoading = false; }
    });
  }

  isTargetSelected(t: CopyTarget): boolean {
    return this.selectedTargets.has(t.timestamp);
  }

  toggleTarget(t: CopyTarget) {
    if (this.copyRunning) { return; }
    if (this.selectedTargets.has(t.timestamp)) {
      this.selectedTargets.delete(t.timestamp);
    } else {
      this.selectedTargets.add(t.timestamp);
    }
  }

  toggleAllTargets() {
    if (this.copyRunning) { return; }
    if (this.selectedTargets.size === this.copyTargets.length) {
      this.selectedTargets.clear();
    } else {
      this.selectedTargets = new Set(this.copyTargets.map((t) => t.timestamp));
    }
  }

  get allTargetsSelected(): boolean {
    return this.copyTargets.length > 0 && this.selectedTargets.size === this.copyTargets.length;
  }

  confirmCopy() {
    if (this.copyRunning || !this.selectedTargets.size) { return; }
    this.copyRunning = true;
    this.copyError = '';
    this.copyDone = false;

    this.copyTargets = this.copyTargets.map((t) => this.selectedTargets.has(t.timestamp)
      ? { ...t, status: 'saving' as const, error: undefined }
      : t);

    let remaining = this.selectedTargets.size;
    for (const ts of Array.from(this.selectedTargets)) {
      this.copyOne(ts, () => {
        remaining -= 1;
        if (remaining === 0) {
          this.copyRunning = false;
          this.copyDone = true;
          const failed = this.copyTargets.filter((t) => t.status === 'error').length;
          if (failed) {
            this.copyError = `Copied to ${this.selectedTargets.size - failed} of ${this.selectedTargets.size}. ${failed} failed.`;
          }
        }
      });
    }
  }

  private copyOne(timestamp: number, done: () => void) {
    const targetKey = buildScanTestKey(this.venueID, timestamp);
    this.apiservice.getScanTests(this.venueID, timestamp).pipe(
      catchError(() => of<ScanTestRecord[]>([]))
    ).subscribe({
      next: (records) => {
        const existing = (records as ScanTestRecord[]).find((r): r is ScanTestMasterConfig => isMasterConfig(r));
        const next: ScanTestMasterConfig = {
          key: targetKey,
          item: 0,
          schedule: existing?.schedule ?? [],
          timezone: existing?.timezone ?? (this.venueTimezone || 'Australia/Sydney'),
          notificationEmails: [...this.emails]
        };
        this.apiservice.postScanTest(next).subscribe({
          next: () => { this.markTarget(timestamp, 'done'); done(); },
          error: (err) => { this.markTarget(timestamp, 'error', err?.error?.message ?? 'Save failed'); done(); }
        });
      },
      error: (err) => {
        this.markTarget(timestamp, 'error', err?.error?.message ?? 'Load failed');
        done();
      }
    });
  }

  private markTarget(timestamp: number, status: CopyTarget['status'], error?: string) {
    this.copyTargets = this.copyTargets.map((t) => t.timestamp === timestamp
      ? { ...t, status, error }
      : t);
  }

  private formatVenueDate(epochSeconds: number): string {
    const tz = this.venueTimezone || 'UTC';
    const date = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      weekday: 'short',
      day: '2-digit',
      month: 'short'
    }).format(new Date(epochSeconds * 1000));
    const time = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(epochSeconds * 1000));
    return `${date} · ${time}`;
  }
}
